import React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { LmsProvider } from './context/LmsContext';
import Layout from './components/Layout';
import { RequireAuth, RequireRole } from './components/RequireAuth';
import Login from './pages/Login';
import Unauthorized from './pages/shared/Unauthorized';
import Profile from './pages/shared/Profile';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminGroups from './pages/admin/AdminGroups';
import AdminCourses from './pages/admin/AdminCourses';
import AdminPrograms from './pages/admin/AdminPrograms';
import AdminCourseDetail from './pages/admin/AdminCourseDetail';
import AdminAssignments from './pages/admin/AdminAssignments';
import AdminReports from './pages/admin/AdminReports';
import AdminCertificates from './pages/admin/AdminCertificates';
import AdminSettings from './pages/admin/AdminSettings';
import TrainerDashboard from './pages/trainer/TrainerDashboard';
import TrainerCourses from './pages/trainer/TrainerCourses';
import NewCourse from './pages/trainer/NewCourse';
import CourseBuilder from './pages/trainer/CourseBuilder';
import QuestionBank from './pages/trainer/QuestionBank';
import TrainerReviews from './pages/trainer/TrainerReviews';
import TrainerGrades from './pages/trainer/TrainerGrades';
import LearnerDashboard from './pages/learner/LearnerDashboard';
import MyCourses from './pages/learner/MyCourses';
import LearnerCourse from './pages/learner/LearnerCourse';
import LearnerProgram from './pages/learner/LearnerProgram';
import LessonPage from './pages/learner/LessonPage';
import QuizPage from './pages/learner/QuizPage';
import ExercisePage from './pages/learner/ExercisePage';
import LearnerGrades from './pages/learner/LearnerGrades';
import LearnerCertificates from './pages/learner/LearnerCertificates';
import { useLms } from './context/LmsContext';
import './styles.css';

function ProfileShell() {
  const { currentUser } = useLms();
  return <Layout role={currentUser?.role || 'learner'} />;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<RequireAuth />}>
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/profile" element={<ProfileShell />}>
            <Route index element={<Profile />} />
          </Route>

          <Route element={<RequireRole role="admin" />}>
            <Route path="/admin" element={<Layout role="admin" />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="groups" element={<AdminGroups />} />
              <Route path="courses" element={<AdminCourses />} />
              <Route path="programs" element={<AdminPrograms />} />
              <Route path="courses/:courseId" element={<AdminCourseDetail />} />
              <Route path="courses/:courseId/builder" element={<CourseBuilder />} />
              <Route path="assignments" element={<AdminAssignments />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="certificates" element={<AdminCertificates />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Route>

          <Route element={<RequireRole role="trainer" />}>
            <Route path="/trainer" element={<Layout role="trainer" />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<TrainerDashboard />} />
              <Route path="courses" element={<TrainerCourses />} />
              <Route path="courses/new" element={<NewCourse />} />
              <Route path="courses/:courseId/builder" element={<CourseBuilder />} />
              <Route path="question-bank" element={<QuestionBank />} />
              <Route path="reviews" element={<TrainerReviews />} />
              <Route path="grades" element={<TrainerGrades />} />
            </Route>
          </Route>

          <Route element={<RequireRole role="learner" />}>
            <Route path="/learner" element={<Layout role="learner" />}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<LearnerDashboard />} />
              <Route path="my-courses" element={<MyCourses />} />
              <Route path="course/:assignmentId" element={<LearnerCourse />} />
              <Route path="program/:programAssignmentId" element={<LearnerProgram />} />
              <Route path="lesson/:lessonProgressId" element={<LessonPage />} />
              <Route path="quiz/:quizAttemptId" element={<QuizPage />} />
              <Route path="exercise/:exerciseSubmissionId" element={<ExercisePage />} />
              <Route path="grades" element={<LearnerGrades />} />
              <Route path="certificates" element={<LearnerCertificates />} />
            </Route>
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <LmsProvider>
      <AppRoutes />
    </LmsProvider>
  );
}
